import React, { Component } from 'react';
import { Link, withRouter } from 'react-router-dom';
import InputField from '../../components/inputs/inputField';
import TextAreaField from '../../components/inputs/textAreaField';
import TabsContainer from '../../components/views/TabsContainer';
import SectionHeader from '../../components/views/SectionHeader';
import SectionFooter from '../../components/views/SectionFooter';
import LoadingWizard from '../../components/spinner/wizard';
import DeleteRegistrantsModal from './modals/deleteRegistrantsModal';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';

const localizer = momentLocalizer(moment);

class RegistrantDetail extends Component {
	constructor(props) {
		super(props);

		this.state = {
			registrant: { ...props.registrant },
			editedRegistrant: { ...props.registrant },
			selectedSection: 'profile',
			editing: false,
			loading: false,
			calendarView: 'month',
			deleteRegistrantsModalOpen: false,
			calendarDate: new Date()
		};
	}

	componentDidMount() {
		this.props.setActiveRow(this.props.registrant._id);
	}

	componentDidUpdate(prevProps) {
		if (prevProps.registrant._id !== this.props.registrant._id) {
			this.setState({
				registrant: { ...this.props.registrant },
				editedRegistrant: { ...this.props.registrant },
				editing: false
			});
			this.props.setActiveRow(this.props.registrant._id);
		}
	}

	editRegistrantField = (property, value) => {
		const editedRegistrant = this.state.editedRegistrant;
		editedRegistrant[property] = value;
		this.setState({ editedRegistrant: editedRegistrant, editing: true });
	};

	toggleEditing = (bool) => {
		this.setState({ editing: bool });
	};

	saveInfo = () => {
		const { editedRegistrant } = this.state;

		const updatedData = {
			firstName: editedRegistrant.firstName,
			lastName: editedRegistrant.lastName,
			emailAddress: editedRegistrant.emailAddress,
			phoneNumber: editedRegistrant.phoneNumber,
			company: editedRegistrant.company,
			notes: editedRegistrant.notes
		};

		this.props.updateRegistrant(editedRegistrant._id, updatedData);

		this.setState({
			editing: false,
			registrant: { ...editedRegistrant }
		});
	};

	cancelEdit = () => {
		const registrant = this.state.registrant;
		this.setState({
			editedRegistrant: { ...registrant },
			editing: false
		});
	};

	updateSelected = (selectedIndex, tabId) => {
		this.setState({ selectedSection: tabId });
	};

	// Calendar methods for schedule view
	getCalendarEvents = () => {
		const { bookingPage } = this.props;
		const { registrant } = this.state;
		const events = [];
		const duration = bookingPage?.meetingDuration || 30;
		const durationUnit = bookingPage?.durationUnit || 'minutes';

		const timeSlotEvents = {};

		const registrantEmail = bookingPage.eventId
			? registrant.attendee?.email
			: registrant.user?.emailAddress;
		const registrantUserId = registrant.user?._id;

		if (bookingPage.calendars && registrantUserId) {
			bookingPage.calendars.forEach(calendar => {
				if (calendar.bookedSpots) {
					calendar.bookedSpots.forEach(spot => {
						if (spot.user === registrantUserId && spot.status !== 'canceled') {
							const key = `${spot.date}-${spot.time}`;
							const startTime = moment(`${spot.date} ${spot.time}`, 'YYYY-MM-DD h:mm A');
							const endTime = startTime.clone().add(duration, durationUnit);

							const event = {
								id: `booked-${spot._id}`,
								title: calendar.name || 'Booking',
								start: startTime.toDate(),
								end: endTime.toDate(),
								type: 'booked',
								resource: {
									calendar: calendar.name || 'Unknown Calendar',
									status: 'booked',
									spotId: spot._id,
									calendarId: calendar._id
								}
							};

							timeSlotEvents[key] = event;
							events.push(event);
						}
					});
				}
			});
		}

		const registrantRequests = bookingPage.requests?.filter(request =>
			request.submission?.formData?.email === registrantEmail
		) || [];

		registrantRequests.forEach(request => {
			if (request.approvedTimes) {
				Object.keys(request.approvedTimes).forEach(date => {
					Object.keys(request.approvedTimes[date]).forEach(calendarId => {
						const calendar = bookingPage.calendars?.find(c => c._id === calendarId);
						request.approvedTimes[date][calendarId].forEach(timeSlot => {
							const key = `${date}-${timeSlot}`;
							
							if (!timeSlotEvents[key]) {
								const startTime = moment(`${date} ${timeSlot}`, 'YYYY-MM-DD h:mm A');
								const endTime = startTime.clone().add(duration, durationUnit);

								const event = {
									id: `approved-${request._id}-${date}-${timeSlot}`,
									title: `${calendar?.name || 'Booking'}`,
									start: startTime.toDate(),
									end: endTime.toDate(),
									type: 'approved',
									resource: {
										calendar: calendar?.name || 'Unknown Calendar',
										status: 'approved',
										requestId: request._id
									}
								};

								timeSlotEvents[key] = event;
								events.push(event);
							}
						});
					});
				});
			}

			if (request.selectedDateTimes) {
				Object.keys(request.selectedDateTimes).forEach(date => {
					Object.keys(request.selectedDateTimes[date]).forEach(calendarId => {
						const calendar = bookingPage.calendars?.find(c => c._id === calendarId);
						request.selectedDateTimes[date][calendarId].forEach(timeSlot => {
							const key = `${date}-${timeSlot}`;
							
							// Check if this slot is already approved or denied
							const isApproved = request.approvedTimes?.[date]?.[calendarId]?.includes(timeSlot);
							const isDenied = request.deniedTimes?.[date]?.[calendarId]?.includes(timeSlot);

							// Only add if not already in timeSlotEvents and not denied
							if (!timeSlotEvents[key] && !isApproved && !isDenied) {
								const startTime = moment(`${date} ${timeSlot}`, 'YYYY-MM-DD h:mm A');
								const endTime = startTime.clone().add(duration, durationUnit);

								events.push({
									id: `pending-${request._id}-${date}-${timeSlot}`,
									title: `${calendar?.name || 'Booking'} (Pending)`,
									start: startTime.toDate(),
									end: endTime.toDate(),
									type: 'pending',
									resource: {
										calendar: calendar?.name || 'Unknown Calendar',
										status: 'pending',
										requestId: request._id
									}
								});
							}
						});
					});
				});
			}
		});

		return events;
	};

	eventStyleGetter = (event) => {
		let backgroundColor = '#2b83f1'; // Platform primary color
		let borderColor = '#2b83f1';

		if (event.type === 'booked' || event.type === 'approved') {
			backgroundColor = '#2b83f1'; // Platform primary for approved bookings
			borderColor = '#2b83f1';
		} else if (event.type === 'pending') {
			backgroundColor = '#999'; // Platform gray for pending requests
			borderColor = '#999';
		}

		return {
			style: {
				backgroundColor,
				borderColor,
				color: 'white',
				border: 'none',
				borderRadius: '4px'
			}
		};
	};

	handleEventSelect = (event) => {
		console.log('Selected event:', event);
		// Could add modal or detail view for event
	};

	toggleDeleteRegistrants = (checkedRegistrants) => {
		this.setState({
			checkedRegistrants: checkedRegistrants || {},
			deleteRegistrantsModalOpen: !this.state.deleteRegistrantsModalOpen,
		});
	};

	getBookingHistory = () => {
		const { bookingPage } = this.props;
		const { registrant } = this.state;

		// Get all requests for this registrant using the correct email field
		const registrantEmail = bookingPage.eventId
			? registrant.attendee?.email
			: registrant.user?.emailAddress;

		const registrantRequests = bookingPage.requests?.filter(request =>
			request.submission?.formData?.email === registrantEmail
		) || [];

		let bookingHistory = [];

		registrantRequests.forEach(request => {
			// Add approved bookings
			if (request.approvedTimes) {
				Object.keys(request.approvedTimes).forEach(date => {
					Object.keys(request.approvedTimes[date]).forEach(calendarId => {
						const calendar = bookingPage.calendars?.find(c => c._id === calendarId);
						request.approvedTimes[date][calendarId].forEach(timeSlot => {
							bookingHistory.push({
								date,
								time: timeSlot,
								calendar: calendar?.name || 'Unknown Calendar',
								status: 'approved',
								requestId: request._id,
								submittedAt: request.submittedAt || request.createdAt
							});
						});
					});
				});
			}

			// Add pending bookings (requested but not approved/denied)
			if (request.selectedDateTimes) {
				Object.keys(request.selectedDateTimes).forEach(date => {
					Object.keys(request.selectedDateTimes[date]).forEach(calendarId => {
						const calendar = bookingPage.calendars?.find(c => c._id === calendarId);
						request.selectedDateTimes[date][calendarId].forEach(timeSlot => {
							// Check if this slot is already approved or denied
							const isApproved = request.approvedTimes?.[date]?.[calendarId]?.includes(timeSlot);
							const isDenied = request.deniedTimes?.[date]?.[calendarId]?.includes(timeSlot);

							if (!isApproved && !isDenied) {
								bookingHistory.push({
									date,
									time: timeSlot,
									calendar: calendar?.name || 'Unknown Calendar',
									status: 'pending',
									requestId: request._id,
									submittedAt: request.submittedAt || request.createdAt
								});
							}
						});
					});
				});
			}
		});



		let calendars = bookingPage.calendars || [];
		let registrantUserId = registrant.user?._id || null;
		let bookedSpotsHistory = [];

		for (let i = 0; i < calendars.length; i++) {
			let calendar = calendars[i];
			let bookedSpots = calendar.bookedSpots || [];
			for (let j = 0; j < bookedSpots.length; j++) {
				let spot = bookedSpots[j];
				if (spot.user === registrantUserId) {
					bookedSpotsHistory.push({
						date: spot.date,
						time: spot.time,
						calendar: calendar.name || 'Unknown Calendar',
						status: spot.status.charAt(0).toUpperCase() + spot.status.slice(1),
						spotId: spot._id
					});
				}

			}
		}

		bookingHistory.sort((a, b) => {
			const dateA = new Date(`${a.date} ${a.time}`);
			const dateB = new Date(`${b.date} ${b.time}`);
			return dateB - dateA; // Most recent first
		});
		bookedSpotsHistory.sort((a, b) => {
			const dateA = new Date(`${a.date} ${a.time}`);
			const dateB = new Date(`${b.date} ${b.time}`);
			return dateB - dateA; // Most recent first
		});


		// Sort by date and time
		return { bookingHistory, bookedSpotsHistory };

	};

	renderSection() {
		const { selectedSection, editing, editedRegistrant, deleteRegistrantsModalOpen } = this.state;
		const { bookingHistory, bookedSpotsHistory } = this.getBookingHistory();

		switch (selectedSection) {
			case 'profile':
				return (
					<div className="sectionContent">
						<h2 className="blockHeader">Contact Information</h2>
						<div className="row">
							<div className="col-6">
								<InputField
									label="First Name"
									value={editedRegistrant.firstName || ''}
									onChange={(e) => this.editRegistrantField('firstName', e.target.value)}
									disabled={true}
									inputIcon={<i className="las la-lock"></i>}
								/>
							</div>
							<div className="col-6">
								<InputField
									label="Last Name"
									value={editedRegistrant.lastName || ''}
									onChange={(e) => this.editRegistrantField('lastName', e.target.value)}
									disabled={true}
									inputIcon={<i className="las la-lock"></i>}
								/>
							</div>
						</div>
						<div className="row">
							<div className="col-12">
								<InputField
									label="Email"
									type="email"
									value={editedRegistrant.emailAddress || ''}
									onChange={(e) => this.editRegistrantField('emailAddress', e.target.value)}
									disabled={true}
									inputIcon={<i className="las la-lock"></i>}
								/>
							</div>
						</div>
						<div className="row">
							<div className="col-6">
								<InputField
									label="Phone Number"
									value={editedRegistrant.phoneNumber || ''}
									onChange={(e) => this.editRegistrantField('phoneNumber', e.target.value)}
									disabled={true}
									inputIcon={<i className="las la-lock"></i>}
								/>
							</div>
							<div className="col-6">
								<InputField
									label="Company"
									value={editedRegistrant.company || ''}
									onChange={(e) => this.editRegistrantField('company', e.target.value)}
									disabled={true}
									inputIcon={<i className="las la-lock"></i>}
								/>
							</div>
						</div>
						<div className="row">
							<div className="col-12">
								<TextAreaField
									label="Notes"
									value={editedRegistrant.notes || ''}
									onChange={(e) => this.editRegistrantField('notes', e.target.value)}
									rows={3}
									placeholder="Add notes about this registrant..."
								/>
							</div>
						</div>
					</div>
				);

			case 'bookings':
				return (
					<div className="sectionContent">
						{/* Booking Statistics */}
						<div className="section mb-30">
							<h2 className="blockHeader">Booking Summary</h2>
							<div className="row">
								<div className="col-4">
									<div className="stat-card">
										<div className="stat-number">{editedRegistrant.totalBookings || 0}</div>
										<div className="stat-label">Total Requests</div>
									</div>
								</div>
								<div className="col-4">
									<div className="stat-card">
										<div className="stat-number text-success">{editedRegistrant.approvedBookings || 0}</div>
										<div className="stat-label">Approved</div>
									</div>
								</div>
								<div className="col-4">
									<div className="stat-card">
										<div className="stat-number text-warning">{editedRegistrant.pendingBookings || 0}</div>
										<div className="stat-label">Pending</div>
									</div>
								</div>
							</div>
							{editedRegistrant.lastBookingDate && (
								<p className="small text-muted mt-10">
									Last booking: {moment(editedRegistrant.lastBookingDate).format('MMM D, YYYY')}
								</p>
							)}
						</div>

						{/* Booking History */}
						<div className="section">
							<h2 className="blockHeader">Request History</h2>
							{bookingHistory.length > 0 ? (
								<div className="booking-history">
									{bookingHistory.map((booking, index) => (
										<div key={index} className="booking-item">
											<div className="flex aic jcsb">
												<div>
													<div className="booking-date">
														{moment(booking.date).format('MMM D, YYYY')} at {booking.time}
													</div>
													<div className="booking-calendar small text-muted">
														{booking.calendar}
													</div>
												</div>
												<div>
													<span className={`status-badge status-${booking.status}`}>
														{booking.status}
													</span>
												</div>
											</div>
										</div>
									))}
								</div>
							) : (
								<div className="empty-state-small">
									<i className="las la-calendar-times"></i>
									<p>No request history yet</p>
								</div>
							)}
						</div>
						<div className="section">
							<h2 className="blockHeader">Booked Spot History</h2>
							{bookedSpotsHistory.length > 0 ? (/*  */
								<div className="booking-history">
									{bookedSpotsHistory.map((booking, index) => (
										<div key={index} className="booking-item">
											<div className="flex aic jcsb">
												<div>
													<div className="booking-date">
														{moment(booking.date).format('MMM D, YYYY')} at {booking.time}
													</div>
													<div className="booking-calendar small text-muted">
														{booking.calendar}
													</div>
												</div>
												<div>
													<span className={`status-badge status-${booking.status}`}>
														{booking.status}
													</span>
												</div>
											</div>
										</div>
									))}
								</div>
							) : (
								<div className="empty-state-small">
									<i className="las la-calendar-times"></i>
									<p>No booking history yet</p>
								</div>
							)}
						</div>
					</div>
				);

			case 'schedule':
				return (
					<div className="sectionContent">
						<div className="sectionBlock unset-w mt-20">
							<h2 className="blockHeader">Schedule View</h2>
							<p className="small mb-20">Calendar view of all approved and pending bookings for this registrant.</p>

							<div style={{ height: 600 }}>
								<Calendar
									localizer={localizer}
									events={this.getCalendarEvents()}
									startAccessor="start"
									endAccessor="end"
									views={['month', 'week', 'day']}
									defaultView="week"
									view={this.state.calendarView}
									date={this.state.calendarDate}
									onView={(view) => this.setState({ calendarView: view })}
									onNavigate={(date) => this.setState({ calendarDate: date })}
									step={30}
									timeslots={2}
									eventPropGetter={this.eventStyleGetter}
									onSelectEvent={this.handleEventSelect}
									onShowMore={(events, date) => {
										this.setState({
											calendarView: 'day',
											calendarDate: date
										});
									}}
									popup
									popupOffset={30}
								/>
							</div>

							{/* Legend */}
							<div className="schedule-legend mt-15">
								<div className="flex gap20">
									<div className="legend-item flex aic">
										<div style={{ width: 16, height: 16, backgroundColor: '#2b83f1', borderRadius: 4, marginRight: 8 }}></div>
										<span>Approved Bookings</span>
									</div>
									<div className="legend-item flex aic">
										<div style={{ width: 16, height: 16, backgroundColor: '#999', borderRadius: 4, marginRight: 8 }}></div>
										<span>Pending Requests</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				);

			default:
				return null;
		}
	}

	render() {
		const { loading, editing, registrant, editedRegistrant, selectedSection, deleteRegistrantsModalOpen } = this.state;
		const { orgId, bookingPage } = this.props;

		const tabs = [
			{
				title: <span>Profile</span>,
				id: 'profile'
			},
			{
				title: <span>Bookings</span>,
				id: 'bookings'
			},
			{
				title: <span>Schedule</span>,
				id: 'schedule'
			}
		];


		return (
			<div className={`sectionContainer`}>
				<div className={`sectionHeader`}>
					<div style={{ width: 30, height: 30 }}>
						<Link to={`./`}>
							<button style={{ padding: 7, marginLeft: -7, marginTop: -7, fontSize: 16 }}>
								<i className='las la-times'></i>
							</button>
						</Link>
					</div>

					<div className="sectionTitleBar">
						<div className='profileIcon mr-15'>
							<h1 className='acr white'>
								{registrant.firstName?.charAt(0) || ''}
								{registrant.lastName?.charAt(0) || ''}
							</h1>
						</div>
						<div className="flex column jcc">
							<h3>{registrant.firstName} {registrant.lastName}</h3>
							{registrant.company && <h4 className='mt-10'>{registrant.company}</h4>}
						</div>
						<div style={{ margin: 'auto 20px', width: 1, height: 40, background: "var(--border)" }}></div>
						<div className='flex column jcc'>
							{registrant.phoneNumber && (
								<h4 className='mb-10'>
									<i className='las la-phone mr-10'></i>
									{registrant.phoneNumber.length === 10
										? `(${registrant.phoneNumber.substring(0, 3)}) ${registrant.phoneNumber.substring(3, 6)}-${registrant.phoneNumber.substring(6, 10)}`
										: registrant.phoneNumber
									}
								</h4>
							)}
							<h4>
								<i className='las la-envelope mr-10'></i>
								<a rel="noreferrer" href={`mailto:${registrant.emailAddress}`} target='_blank'>
									{registrant.emailAddress}
								</a>
							</h4>
						</div>

						<div className="ml-a">
							<SectionHeader subActions={[{
										label: 'Delete',
										iconClass: 'las la-trash-alt',
										class: 'danger',
										onClick: this.toggleDeleteRegistrants
									}]} />
						</div>
					</div>

					<div className="sectionHeaderChildren">
						<TabsContainer
							tabs={tabs}
							updateSelected={this.updateSelected}
							selectedIndex={tabs.findIndex((t) => t.id === selectedSection)}
						/>
					</div>
				</div>

				<div className="sectionBody">
					{!loading && this.renderSection()}
				</div>

				<LoadingWizard text="Loading Registrant" loading={loading} />

				{deleteRegistrantsModalOpen && (
					<DeleteRegistrantsModal
						isOpen={deleteRegistrantsModalOpen}
						delete={() => this.props.deleteSingleRegistrant(this.props.registrant._id)}
						toggle={() => {
							this.toggleDeleteRegistrants();
							this.props.history.push('./');
						}}
						orgId={orgId}
					/>
				)}

				<SectionFooter
					updatedAt={registrant.updatedAt}
					cancelEdit={this.cancelEdit}
					saveEdit={this.saveInfo}
					editing={editing}
					lastEditedBy={registrant.lastEditedBy}
				/>
			</div>
		);
	}
}

export default withRouter(RegistrantDetail);
